import React, { useCallback, useEffect, useRef, useState } from "react";
import { getGeocode } from "use-places-autocomplete";

import { CircularIndeterminate, FormButton, Mapa } from "components";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    rowSelected,
    snackbarData,
    userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import ModalMapa from "components/utils/mapa/ModalMapa";
import { Grid } from "@mui/material";
import Modal from "components/UI/Modal/Modal";
import { inputTextRegexp } from "utils/regexp";
import { SFLocalidadGet as localidadGet } from "services/services-mia/SFHotel/SFLocalidadGet";
import { hotelUpd } from "services/services-mia/SFHotel/SFHotelUpd";
import { AddLocationAltOutlined } from "@mui/icons-material";

export default function UpdateModal({ updateList, hotel, closeModal, provincias = [], isViewDetail }) {
    const [inRequest, setInRequest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [localidades, setLocalidades] = useState([]);
    const [modalMapaOpen, setModalMapaOpen] = useState(false);
    const [coordenadas, setCoordenadas] = useState({});

    const setSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const formikRef = useRef();

    const [isGeocoding, setIsGeocoding] = useState(false);
    const lastGeocodedRef = useRef("");

    const normalizeNombre = (str) =>
        str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() ?? "";

    const matchesNombre = (nombre, termino) => {
        const a = normalizeNombre(nombre);
        const b = normalizeNombre(termino);
        return a.includes(b) || b.includes(a);
    };

    const getSantaFeProvincia = () =>
        provincias.find((p) => matchesNombre(p.provinciaNombre, "santa fe"));

    const selectProvincia = useCallback(({ provinciaSantaFeId, provinciaNombre }) => {
        formikRef.current?.setFieldValue("provincia", provinciaSantaFeId);
        formikRef.current?.setFieldValue("provinciaNombre", provinciaNombre);
    }, []);

    const selectLocalidad = useCallback(({ localidadSantafeId, localidadSantafeNombre }) => {
        formikRef.current?.setFieldValue("localidad", localidadSantafeId);
        formikRef.current?.setFieldValue("localidadNombre", localidadSantafeNombre);
    }, []);

    const getLocalidades = useCallback(async (provinciaSantaFeId, { selectFirst = false } = {}) => {
        const provinciaId = Number(provinciaSantaFeId);
        if (!Number.isFinite(provinciaId)) return;

        setIsLoading(true);
        setLocalidades([]);
        formikRef.current?.setFieldValue("localidad", "");

        const { result, status } = await localidadGet({ provinciaId }, credentials);

        if (status.code !== 1) {
            setIsLoading(false);
            return;
        }
        setLocalidades(result);

        if (selectFirst && result[0]) {
            selectLocalidad(result[0]);
        } else if (hotel?.localidadNombre) {
            const current = result.find((r) =>
                matchesNombre(r.localidadSantafeNombre, hotel.localidadNombre)
            );
            if (current) {
                selectLocalidad(current);
            } else if (result[0]) {
                selectLocalidad(result[0]);
            }
        } else if (result[0]) {
            selectLocalidad(result[0]);
        }
        setIsLoading(false);
    }, [credentials, hotel, selectLocalidad]);

    function GeocodeWatcher() {
        const { values } = useFormikContext();

        useEffect(() => {
            let mounted = true;
            const calle = (values?.calle || "").trim();
            const numero = (values?.numero || "").toString().trim();
            const localidadName = values?.localidadNombre || values?.localidad || "";
            const provinciaName = values?.provinciaNombre || values?.provincia || "";

            console.log('UpdateModal - watcher values change:', { calle, numero, localidadName, provinciaName });

            if (!calle || calle.length < 3 || !numero) return;

            const composed = `${calle} ${numero}${localidadName ? `, ${localidadName}` : ""}${provinciaName ? `, ${provinciaName}` : ""}, Argentina`;
            if (composed === lastGeocodedRef.current) {
                console.log('UpdateModal - composed equals lastGeocoded, skipping:', composed);
                return;
            }

            const timer = setTimeout(async () => {
                if (!mounted) return;
                console.log('UpdateModal - geocode triggered for:', composed);
                try {
                    setIsGeocoding(true);
                    const results = await getGeocode({ address: composed });
                    if (results && results[0]) {
                        const lat = results[0].geometry.location.lat();
                        const lng = results[0].geometry.location.lng();
                        console.log('UpdateModal - geocode result:', { lat, lng });
                        setCoordenadas({ latitud: lat, longitud: lng });
                        if (formikRef.current) {
                            formikRef.current.setFieldValue("latitud", Number(lat).toFixed(6));
                            formikRef.current.setFieldValue("longitud", Number(lng).toFixed(6));
                        }
                        lastGeocodedRef.current = composed;
                    } else {
                        console.log('UpdateModal - geocode returned no results for:', composed);
                    }
                } catch (err) {
                    console.error("UpdateModal - Geocode error:", err);
                } finally {
                    if (mounted) setIsGeocoding(false);
                }
            }, 1000);

            return () => {
                mounted = false;
                clearTimeout(timer);
            };
        }, [values?.calle, values?.numero, values?.localidadNombre, values?.provinciaNombre]);

        return null;
    }

    useEffect(() => {
        if (Object.keys(credentials).length === 0 || !provincias.length) return;

        console.log(provincias)

        let active = true;
        (async () => {
            await Promise.resolve();
            if (!active) return;

            setCoordenadas({
                latitud: hotel?.SFHotelLatitud ?? -32.9442426,
                longitud: hotel?.SFHotelLongitud ?? -60.6504542,
            });

            // Buscamos la provincia del hotel, o Santa Fe por defecto
            const provinciaIdTarget = hotel?.provinciaId;
            const provincia = provincias.find(p => String(p.provinciaSantaFeId) === String(provinciaIdTarget))
                || getSantaFeProvincia();

            if (provincia?.provinciaSantaFeId) {
                selectProvincia(provincia);
                await getLocalidades(provincia.provinciaSantaFeId);
            }
        })();

        console.log(hotel);

        return () => {
            active = false;
        };
    }, [credentials, provincias, hotel, getLocalidades, selectProvincia]);



    const handleChangeProvincia = (event) => {
        const currentProvincia = provincias.find(
            (provincia) => String(provincia.provinciaSantaFeId) === String(event.target.value)
        );
        if (!currentProvincia?.provinciaSantaFeId) return;

        selectProvincia(currentProvincia);
        getLocalidades(currentProvincia.provinciaSantaFeId, { selectFirst: true });
    }

    const handleChangeLocalidad = (event) => {
        const localidadId = Number(event.target.value);
        const currentLocalidad = localidades.find((localidad) => (localidad.localidadSantafeId === localidadId))
        selectLocalidad(currentLocalidad ?? {})
    }

    const handleSetCoordenadas = ({ latitud, longitud, calle, numero, localidad, cod }) => {
        setCoordenadas({
            latitud,
            longitud
        })
        if (latitud) formikRef.current.setFieldValue("latitud", latitud.toFixed(6));
        if (longitud) formikRef.current.setFieldValue("longitud", longitud.toFixed(6));
        if (calle) formikRef.current.setFieldValue("calle", calle);
        if (numero) formikRef.current.setFieldValue("numero", numero);
        if (cod) formikRef.current.setFieldValue("cod", cod);

        if (localidad) {
            const currentLocalidad = localidades.find((l) =>
                matchesNombre(l.localidadSantafeNombre, localidad)
            );
            console.log("UPDATE - Localidad buscada:", localidad);
            console.log("UPDATE - Localidad encontrada:", currentLocalidad);

            if (currentLocalidad) {
                selectLocalidad(currentLocalidad);
            }
        }
    }

    const formatCuitManual = (value) => {
        // Solo permitimos números
        const onlyNums = value.replace(/\D/g, "");
        // Limitamos a 11 dígitos (el máximo de un CUIT)
        const d = onlyNums.slice(0, 11);

        // Construimos el string basándonos en la cantidad de números que hay
        if (d.length <= 2) {
            return d;
        } else if (d.length <= 10) {
            // Formateamos a: nn-nnnnnnnn
            return `${d.slice(0, 2)}-${d.slice(2)}`;
        } else {
            // Formateamos a: nn-nnnnnnnn-n
            return `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10, 11)}`;
        }
    }

    const handleChangeCuit = (e) => {
        const { value } = e.target;
        const formattedValue = formatCuitManual(value);
        formikRef.current.setFieldValue("cuit", formattedValue);
    };

    const handleToUpperCase = (fieldName) => (e) => {
        const { value } = e.target;
        formikRef.current.setFieldValue(fieldName, value.toUpperCase());
    };

    const handleSubmit = async ({
        nombre,
        calle,
        localidad,
        numero,
        cuit,
        cod,
        checkin,
        checkout,
        email,
        telefono,
    }) => {
        setInRequest(true);
        const response = await hotelUpd({
            SFHotelId: hotel?.SFHotelId,
            SFHotelNombre: nombre,
            SFHotelDomicilioCalle: calle,
            SFHotelDomicilioCasa: numero,
            SFHotelDomicilioCPA: cod,
            SFHotelEmail: email,
            SFHotelTelefono: telefono,
            localidadId: localidad,
            SFHotelLatitud: coordenadas.latitud ? coordenadas.latitud.toFixed(6) : hotel?.SFHotelLatitud,
            SFHotelLongitud: coordenadas.longitud ? coordenadas.longitud.toFixed(6) : hotel?.SFHotelLongitud,
            SFHotelCUIT: cuit,
            SFHotelCheckin: checkin,
            SFHotelCheckout: checkout,
            SFHotelVersion: hotel?.SFHotelVersion,
            SFHotelStatus: hotel?.SFHotelStatus,
        }, credentials);

        const functions = {
            setSnackbarInfo,
            setIsOpen: closeModal,
            setSelected,
            updateList,
        };

        handleResponse(
            response,
            "Hotel actualizado exitosamente",
            functions
        );

        setInRequest(false);
    };

    const closeModalMapa = () => {
        setModalMapaOpen(false)
    }
    const cuitREGEX = /^\d{2}-\d{8}-\d{1}$/;

    const initialValues = {
        nombre: hotel?.SFHotelNombre ?? "",
        provincia: hotel?.provinciaId ?? "",
        localidad: hotel?.localidadId ?? "",
        calle: hotel?.SFHotelDomicilioCalle ?? "",
        numero: hotel?.SFHotelDomicilioCasa ?? "",
        cod: hotel?.SFHotelDomicilioCPA ?? "",
        cuit: hotel?.SFHotelCUIT ?? "",
        checkin: hotel?.SFHotelCheckin ?? "",
        checkout: hotel?.SFHotelCheckout ?? "",
        email: hotel?.SFHotelEmail ?? "",
        telefono: hotel?.SFHotelTelefono ?? "",
        latitud: hotel?.SFHotelLatitud != null && hotel?.SFHotelLatitud !== ""
            ? Number(hotel.SFHotelLatitud).toFixed(6)
            : "",
        longitud: hotel?.SFHotelLongitud != null && hotel?.SFHotelLongitud !== ""
            ? Number(hotel.SFHotelLongitud).toFixed(6)
            : ""
    }

    const validationSchema = yup.object({
        nombre: yup.string(FORM_ERROR_MESSAGES.text)
            .min(2, "Debe tener minimo 2 caracteres")
            .max(45, "Debe tener un maximo de 45 caracteres")
            .matches(
                inputTextRegexp,
                "Debe tener solo letras y/o números"
            )
            .required(FORM_ERROR_MESSAGES.required),
        calle: yup.string(FORM_ERROR_MESSAGES.text)
            .min(2, "Debe tener minimo 2 caracteres")
            .max(45, "Debe tener un maximo de 45 caracteres")
            .matches(
                inputTextRegexp,
                "Debe tener solo letras y/o números"
            )
            .required(FORM_ERROR_MESSAGES.required),
        numero: yup.string(FORM_ERROR_MESSAGES.text)
            .required(FORM_ERROR_MESSAGES.required),
        cuit: yup.string(FORM_ERROR_MESSAGES.text)
            .required(FORM_ERROR_MESSAGES.required)
            .test("cuit", "CUIT inválido", function (value) {
                if (!value) return true;
                if (!cuitREGEX.test(value)) return false;
                return true;
            }),
        cod: yup.string(FORM_ERROR_MESSAGES.text)
            .max(15, "Debe tener un maximo de 15 caracteres")
            .matches(
                inputTextRegexp,
                "Debe tener solo letras y/o números"
            )
            .required(FORM_ERROR_MESSAGES.required),
        checkin: yup.string(FORM_ERROR_MESSAGES.text)
            .required(FORM_ERROR_MESSAGES.required),
        checkout: yup.string(FORM_ERROR_MESSAGES.text)
            .required(FORM_ERROR_MESSAGES.required)
            .test("is-after-checkout", "El check-out debe ser menor al check-in", function (value) {
                if (value && this.parent.checkin) {
                    return value < this.parent.checkin;
                }
                return true;
            })
    })

    return (
        <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}

            onSubmit={(values, { setSubmitting }) => {
                const trimmedValues = Object.fromEntries(
                    Object.entries(values).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
                );
                values.latitud = coordenadas.latitud.toFixed(6)
                values.longitud = coordenadas.longitud.toFixed(6)
                try {
                    handleSubmit(trimmedValues);
                } catch (e) {
                    setSnackbarInfo({
                        message: e.errmsg,
                        severity: "error",
                        open: true,
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            <Form className={styles.inputs} noValidate >
                <GeocodeWatcher />
                <Modal onClose={closeModalMapa} open={modalMapaOpen} title="Seleccionar coordenadas">
                    {modalMapaOpen && <ModalMapa
                        closeModal={closeModalMapa}
                        latitud={coordenadas.latitud}
                        longitud={coordenadas.longitud}
                        calle={formikRef?.current?.values?.calle}
                        numero={formikRef?.current?.values?.numero}
                        localidad={formikRef?.current?.values?.localidadNombre}
                        provincia={formikRef?.current?.values?.provinciaNombre}
                        cod={formikRef?.current?.values?.cod}
                        onSetCoordenadas={handleSetCoordenadas}
                    />}
                </Modal>
                <div className={styles.column}>
                    <Grid container spacing={4} sx={{ alignItems: "start", minWidth: { md: isViewDetail ? "80vw" : "" }, paddingY: { md: isViewDetail ? "20px" : "" } }}>

                        <Grid item md={6}>

                            <Grid item md={12}>

                                <Grid container spacing={3}>

                                    <Grid item md={6}>
                                        <FormikTextInput
                                            name="nombre"
                                            labelText={"Hotel"}
                                            placeholder="Hotel Name"
                                            isViewDetail={isViewDetail}
                                            inputProps={{ maxLength: 45 }}
                                        />
                                    </Grid>
                                    <Grid item md={6}>
                                        <FormikTextInput
                                            name="cuit"
                                            labelText={"CUIT"}
                                            placeholder="00-00000000-0"
                                            onChange={handleChangeCuit}
                                            isViewDetail={isViewDetail}
                                        />
                                    </Grid>

                                </Grid>

                            </Grid>

                            <Grid item md={12}>

                                <Grid container spacing={3}>
                                    <Grid item md={6}>
                                        <FormikSelectInput
                                            name="provincia"
                                            labelText="Provincia"
                                            onChange={handleChangeProvincia}
                                            isViewDetail={isViewDetail}
                                        >
                                            {provincias.map(({ provinciaSantaFeId, provinciaNombre }) => (
                                                <option
                                                    key={provinciaSantaFeId}
                                                    data-id={provinciaSantaFeId}
                                                    value={provinciaSantaFeId}
                                                >
                                                    {provinciaNombre}
                                                </option>
                                            ))}
                                        </FormikSelectInput>
                                    </Grid>
                                    <Grid item md={6}>
                                        <FormikSelectInput
                                            name="localidad"
                                            labelText="Localidad"
                                            onChange={handleChangeLocalidad}
                                            isViewDetail={isViewDetail}
                                        >
                                            {localidades.map(({ localidadSantafeId, localidadSantafeNombre }) => (
                                                <option
                                                    key={localidadSantafeId}
                                                    data-id={localidadSantafeId}
                                                    value={localidadSantafeId}
                                                >
                                                    {localidadSantafeNombre}
                                                </option>
                                            ))}
                                        </FormikSelectInput>
                                    </Grid>

                                </Grid>

                            </Grid>


                            <Grid item md={12}>

                                <Grid container spacing={3}>

                                    <Grid item md={6}>
                                        <FormikTextInput
                                            name="calle"
                                            labelText={"Calle"}
                                            isViewDetail={isViewDetail}
                                            inputProps={{ maxLength: 45 }}
                                        />
                                    </Grid>
                                    <Grid item md={3}>
                                        <FormikTextInput
                                            name="numero"
                                            labelText={"Número"}
                                            inputProps={{ maxLength: 10 }}
                                            isViewDetail={isViewDetail}
                                        />
                                    </Grid>
                                    <Grid item md={3}>
                                        <FormikTextInput
                                            name="cod"
                                            labelText={"CPA"}
                                            placeholder="Ingrese el código postal"
                                            isViewDetail={isViewDetail}
                                            inputProps={{ maxLength: 8, style: { textTransform: 'uppercase' } }}
                                            onChange={handleToUpperCase("cod")}
                                        />
                                    </Grid>

                                </Grid>

                            </Grid>


                            <Grid item md={12}>

                                <Grid container spacing={3}>
                                    <Grid item md={6}>
                                        <FormikTextInput
                                            type={"time"}
                                            name="checkin"
                                            labelText={"Check-in"}
                                            isViewDetail={isViewDetail}
                                        />
                                    </Grid>
                                    <Grid item md={6}>
                                        <FormikTextInput
                                            type={"time"}
                                            name="checkout"
                                            labelText={"Check-out"}
                                            isViewDetail={isViewDetail}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>


                            <Grid item md={12}>
                                <Grid container spacing={3}>
                                    <Grid item md={6}>
                                        <FormikTextInput
                                            name="latitud"
                                            labelText="Latitud"
                                            isViewDetail={true}
                                        />
                                    </Grid>
                                    <Grid item md={6}>
                                        <FormikTextInput
                                            name="longitud"
                                            labelText="Longitud"
                                            isViewDetail={true}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>


                        </Grid>

                        <Grid item md={6}>
                            <Grid item md={12} height={350}>
                                <div className={styles.mapPanel}>
                                    <div className={styles.mapContainer}>
                                        {!modalMapaOpen && (
                                            <Mapa
                                                lat={coordenadas.latitud}
                                                lng={coordenadas.longitud}
                                                style={{ position: "absolute", inset: 0 }}
                                            />
                                        )}
                                    </div>
                                    {!isViewDetail && (
                                        <button
                                            type="button"
                                            className="map-btn"
                                            onClick={() => setModalMapaOpen(true)}
                                            style={{ margin: "0.5rem", border: "none", display: "flex", alignItems: "center", gap: "0.2rem", backgroundColor: "transparent", cursor: "pointer" }}
                                        >
                                            <AddLocationAltOutlined sx={{ width: "18px", height: "18px" }} /> <span style={{ borderBottom: "1px solid #115D8D", fontWeight: "bold" }}>Buscar coordenadas</span>
                                        </button>
                                    )}
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                    {!isViewDetail && (
                        <FormButton
                            inRequest={inRequest || isLoading}
                            newFormData={{ button: "Guardar cambios" }}
                            onCloseModal={closeModal}
                        />
                    )}

                    {(inRequest || isLoading) && <CircularIndeterminate />}
                </div>
            </Form>
        </Formik>
    );
}
